/**
 * i18n Maisons Bocuse — repose sur @alterrego/core-i18n (moteur framework-agnostic
 * partagé par toutes les apps). On ajoute ici les chaînes propres au portail + un
 * petit hook React (useI18n) qui rerend les composants au changement de langue,
 * et la résolution de langue au démarrage (?lang= → stockage → navigateur → fr).
 *
 * On NE copie PAS le setup react-i18next de CCHMV : core-i18n est la brique
 * commune, ce qui profitera aux autres apps quand elles migreront.
 */
import { useEffect, useState } from 'react';
import {
  addTranslations,
  getLang,
  loadLang,
  onLangChange,
  setI18nStorage,
  setLang,
  t,
  type Lang,
} from '@alterrego/core-i18n';
import tenantConfig from '../tenant.config.json';

const STORAGE_KEY = 'maisons-bocuse-lang';

/** Langues déclarées par le tenant (1re = défaut). */
export const APP_LANGS = (tenantConfig.languages as Lang[]) ?? ['fr'];
const DEFAULT_LANG: Lang = APP_LANGS[0] ?? 'fr';

export const LANG_LABEL: Record<string, string> = { fr: 'FR', en: 'EN' };

// Chaînes propres au portail Bocuse. Ton éditorial : phrases courtes, français
// cultivé, jamais marketing. EN = miroir, à enrichir avec le contenu officiel.
addTranslations({
  // — Hero / portail —
  hero_overline: { fr: 'Maisons', en: 'Maisons' },
  hero_subtitle: {
    fr: "Les brasseries et restaurants fondés par Paul Bocuse, à Lyon et alentour.",
    en: 'The brasseries and restaurants founded by Paul Bocuse, in and around Lyon.',
  },
  back: { fr: 'Retour', en: 'Back' },

  // — Tuiles —
  tile_maisons: { fr: 'Nos maisons', en: 'Our houses' },
  tile_maisons_sub: { fr: 'Les tables de la maison', en: 'The tables of the house' },
  tile_giftcard: { fr: 'Carte cadeau', en: 'Gift card' },
  tile_giftcard_sub: { fr: 'Offrir une table', en: 'Offer a table' },
  tile_universe: { fr: "L'univers Bocuse", en: 'The Bocuse world' },
  tile_universe_sub: { fr: 'Héritage et savoir-faire', en: 'Heritage and craft' },

  // — Liste maisons —
  maisons_title: { fr: 'Nos maisons', en: 'Our houses' },
  maisons_intro: {
    fr: 'Les adresses de la maison Bocuse à Lyon et ses environs.',
    en: 'The addresses of the Bocuse house in and around Lyon.',
  },
  maisons_loading: { fr: 'Chargement…', en: 'Loading…' },
  maisons_error: {
    fr: 'Les maisons n’ont pas pu être chargées.',
    en: 'The houses could not be loaded.',
  },
  maisons_empty: { fr: 'Aucune maison à afficher.', en: 'No house to display.' },
  maisons_count_one: { fr: 'maison', en: 'house' },
  maisons_count_many: { fr: 'maisons', en: 'houses' },

  // — Fiche détail —
  detail_address: { fr: 'Adresse', en: 'Address' },
  detail_phone: { fr: 'Téléphone', en: 'Phone' },
  detail_contact: { fr: 'Contact', en: 'Contact' },
  detail_hours: { fr: 'Horaires', en: 'Opening hours' },
  detail_closed: { fr: 'Fermé', en: 'Closed' },
  detail_reserve: { fr: 'Réserver une table', en: 'Book a table' },
  detail_reserve_soon: {
    fr: 'Réservation en ligne bientôt disponible.',
    en: 'Online booking coming soon.',
  },
  detail_notfound: { fr: 'Maison introuvable.', en: 'House not found.' },
  detail_close: { fr: 'Fermer', en: 'Close' },

  // — Rubriques en préparation —
  soon_title: { fr: 'Bientôt disponible', en: 'Coming soon' },
  soon_body: {
    fr: 'Cette page est en préparation.',
    en: 'This page is being prepared.',
  },

  // ─────────────────────────────────────────────────────────────
  //  Carte cadeau — maquette (présentation + parcours d'achat)
  // ─────────────────────────────────────────────────────────────

  // — Présentation (Mode 1) —
  gc_eyebrow: { fr: 'Carte cadeau', en: 'Gift card' },
  gc_present_tagline: {
    fr: 'Une carte, toutes nos tables.',
    en: 'One card, all our tables.',
  },
  gc_flip_hint: {
    fr: 'Touchez la carte pour la retourner',
    en: 'Tap the card to flip it',
  },
  gc_reloadable: { fr: 'Rechargeable', en: 'Rechargeable' },
  gc_cta_buy: { fr: 'Acheter une carte cadeau', en: 'Buy a gift card' },

  // — Visuel de la carte (recto / verso) —
  card_overline: { fr: 'MAISONS', en: 'MAISONS' },
  card_voucher: { fr: 'BON CADEAU', en: 'GIFT VOUCHER' },
  card_back_title: {
    fr: 'CARTE CADEAU RECHARGEABLE',
    en: 'RECHARGEABLE GIFT CARD',
  },
  card_back_valid: {
    fr: 'Valable dans toutes les Maisons Bocuse *',
    en: 'Valid in all Maisons Bocuse *',
  },
  card_back_exclusion: {
    fr: '*Hors Restaurant Paul Bocuse étoilé et hors achat en ligne',
    en: '*Excluding the starred Restaurant Paul Bocuse and online purchases',
  },
  card_back_code: { fr: 'CODE', en: 'CODE' },
  card_back_number: { fr: 'CARTE N°', en: 'CARD No.' },
  card_back_use_title: { fr: 'UTILISATION', en: 'USE' },
  card_back_use_body: {
    fr: 'Présentez cette carte ou son QR code en caisse.',
    en: 'Show this card or its QR code at checkout.',
  },
  card_back_conditions_title: { fr: 'CONDITIONS', en: 'CONDITIONS' },
  card_back_conditions_body: {
    fr: 'Non remboursable. Valable 12 mois.',
    en: 'Non-refundable. Valid for 12 months.',
  },
  card_quote: {
    fr: 'À table avec des amis le temps ne compte plus.',
    en: 'At the table with friends, time no longer counts.',
  },

  // — Parcours d'achat — barre & navigation (Mode 2) —
  gc_step: { fr: 'Étape', en: 'Step' },
  gc_continue: { fr: 'Continuer', en: 'Continue' },
  gc_cancel: { fr: 'Annuler', en: 'Cancel' },

  // — Étape 1 : montant —
  gc_amount_title: {
    fr: 'Quel montant souhaitez-vous offrir ?',
    en: 'How much would you like to give?',
  },
  gc_amount_other: { fr: 'Autre montant', en: 'Other amount' },
  gc_amount_custom_label: {
    fr: 'Saisissez un montant',
    en: 'Enter an amount',
  },
  gc_amount_range_prefix: { fr: 'Montant libre :', en: 'Free amount:' },
  gc_amount_invalid: {
    fr: 'Choisissez un montant valide pour continuer.',
    en: 'Choose a valid amount to continue.',
  },

  // — Étape 2 : destinataire —
  gc_recipient_title: { fr: 'Pour qui ?', en: 'Who is it for?' },
  gc_recipient_other: { fr: 'Pour quelqu’un', en: 'For someone else' },
  gc_recipient_other_sub: {
    fr: 'Envoi par email au destinataire',
    en: 'Sent by email to the recipient',
  },
  gc_recipient_self: { fr: 'Pour moi', en: 'For me' },
  gc_recipient_self_sub: {
    fr: 'Vous recevez la carte par email',
    en: 'You receive the card by email',
  },
  gc_recipient_name: { fr: 'Nom du destinataire', en: 'Recipient’s name' },
  gc_recipient_name_ph: { fr: 'Camille Martin', en: 'Camille Martin' },
  gc_recipient_email: { fr: 'Email du destinataire', en: 'Recipient’s email' },
  gc_recipient_self_email: { fr: 'Votre email', en: 'Your email' },
  gc_recipient_email_ph: { fr: 'camille@exemple.fr', en: 'camille@example.com' },
  gc_recipient_date: {
    fr: 'Date de livraison (optionnel)',
    en: 'Delivery date (optional)',
  },

  // — Étape 3 : message —
  gc_message_title: {
    fr: 'Un mot pour accompagner ?',
    en: 'A word to go with it?',
  },
  gc_message_optional: { fr: 'Facultatif', en: 'Optional' },
  gc_message_ph: {
    fr: 'Joyeux anniversaire / Pour fêter ça / Merci pour tout…',
    en: 'Happy birthday / To celebrate / Thank you for everything…',
  },

  // — Étape 4 : récapitulatif —
  gc_review_title: { fr: 'Récapitulatif', en: 'Summary' },
  gc_review_amount: { fr: 'Montant', en: 'Amount' },
  gc_review_recipient: { fr: 'Destinataire', en: 'Recipient' },
  gc_review_recipient_self: { fr: 'Vous-même', en: 'Yourself' },
  gc_review_message: { fr: 'Message', en: 'Message' },
  gc_review_message_none: { fr: 'Aucun message', en: 'No message' },
  gc_review_date: { fr: 'Envoi', en: 'Delivery' },
  gc_review_date_now: { fr: 'Dès le paiement', en: 'Right after payment' },
  gc_review_note: {
    fr: 'Carte numérique envoyée par email. Ajout possible à Apple Wallet / Google Wallet.',
    en: 'Digital card sent by email. Can be added to Apple Wallet / Google Wallet.',
  },
  gc_pay: { fr: 'Procéder au paiement', en: 'Proceed to payment' },

  // — Confirmation (maquette) —
  gc_confirm_eyebrow: { fr: 'Commande confirmée', en: 'Order confirmed' },
  gc_confirm_thanks: { fr: 'Merci', en: 'Thank you' },
  gc_confirm_sent_to: {
    fr: 'Votre carte cadeau sera envoyée à',
    en: 'Your gift card will be sent to',
  },
  gc_confirm_on: { fr: 'le', en: 'on' },
  gc_confirm_to_you: {
    fr: 'Votre carte cadeau vous sera envoyée',
    en: 'Your gift card will be sent to you',
  },
  gc_confirm_mockup_note: {
    fr: 'Maquette — aucun paiement réel n’a été effectué.',
    en: 'Mockup — no real payment was made.',
  },
  gc_confirm_wallet_btn: { fr: 'Voir un aperçu Wallet', en: 'Preview the Wallet pass' },
  gc_confirm_done: { fr: 'Terminer', en: 'Done' },

  // — Aperçu pass Wallet —
  gc_wallet_title: { fr: 'Aperçu du pass', en: 'Pass preview' },
  gc_wallet_value: { fr: 'Valeur', en: 'Value' },
  gc_wallet_soon: {
    fr: 'Le pass Apple Wallet / Google Wallet arrivera en V2.',
    en: 'The Apple Wallet / Google Wallet pass is coming in V2.',
  },
  gc_wallet_close: { fr: 'Fermer', en: 'Close' },
});

/** Normalise un code langue vers une langue supportée par l'app. */
function normalize(raw: string | null | undefined): Lang | null {
  if (!raw) return null;
  const short = raw.slice(0, 2).toLowerCase() as Lang;
  return APP_LANGS.includes(short) ? short : null;
}

/**
 * Résout et applique la langue au démarrage. Priorité :
 * ?lang= (et on persiste) → langue stockée → langue navigateur → défaut tenant.
 * À appeler une fois avant le 1er render.
 */
export async function initI18n(): Promise<void> {
  setI18nStorage({
    get: async () => localStorage.getItem(STORAGE_KEY),
    set: async (lang) => localStorage.setItem(STORAGE_KEY, lang),
  });

  const params = new URLSearchParams(window.location.search);
  const fromQuery = normalize(params.get('lang'));
  if (fromQuery) {
    setLang(fromQuery);
    return;
  }

  await loadLang(); // applique la langue stockée si présente
  const stored = normalize(localStorage.getItem(STORAGE_KEY));
  if (stored) return;

  const fromNav = normalize(navigator.language);
  setLang(fromNav ?? DEFAULT_LANG);
}

/** Hook React : expose t(), la langue courante et un setter, et rerend au changement. */
export function useI18n() {
  const [lang, setLangState] = useState<Lang>(getLang());
  useEffect(() => onLangChange(setLangState), []);
  return { t, lang, setLang };
}
